// Logo TB Trading — PNG dentro de un container dark sutil. Usa object-fit
// cover + object-position para centrar el área activa del logo (sin padding
// negro nativo de la imagen).

import type { ReactElement } from 'react';
import { cn } from '../lib/cn';

interface Props {
  size?: number;
  showWordmark?: boolean;
  className?: string;
  variant?: 'sidebar' | 'auth' | 'flat';
  shimmer?: boolean;
}

export function BiniLogo({
  size = 36,
  showWordmark = false,
  className = '',
  variant: _variant = 'sidebar',
  shimmer: _shimmer = true,
}: Props): ReactElement {
  // Modo collapsed / favicon: SVG mark cuadrado simple.
  if (!showWordmark) {
    return (
      <div
        className={cn(
          'relative shrink-0 inline-block rounded-lg overflow-hidden bg-stone-950',
          className,
        )}
        style={{ width: size, height: size }}
      >
        <BiniMarkSquareSVG />
      </div>
    );
  }

  // Modo expanded: container landscape mostrando el logo principal centrado.
  // Aspect 2.2:1 da espacio horizontal suficiente para que bull + TB + bear
  // se vean completos sin cortar los lados.
  const width = Math.round(size * 5.4);
  const height = Math.round(width / 2.2);
  return (
    <div
      className={cn(
        'relative inline-block rounded-lg overflow-hidden bg-stone-950',
        className,
      )}
      style={{ width, height }}
      aria-label="TB Trading"
    >
      <img
        src="/logo.png"
        alt=""
        draggable={false}
        className="absolute inset-0 w-full h-full pointer-events-none select-none"
        style={{
          objectFit: 'cover',
          // El logo principal (TB+bull+bear+arrow+TRADING) ocupa centro óptico
          // alrededor de Y ≈ 42% de la imagen 1:1. cover + 42% lo centra
          // verticalmente sin mostrar la firma Binimeliz.
          objectPosition: 'center 42%',
        }}
      />
    </div>
  );
}

// Hero "completo" para login splash — PNG con TODO incluyendo Binimeliz
// cursiva. Container con aspect 1.45:1 que da espacio para mostrar el logo
// principal + tagline + firma sin sobrar padding negro.
export function BiniLogoHero({ maxWidth = 440 }: { maxWidth?: number }): ReactElement {
  return (
    <div
      className="relative mx-auto rounded-2xl overflow-hidden bg-stone-950 shadow-[0_24px_64px_-16px_rgb(16_185_129_/_0.22)]"
      style={{
        maxWidth,
        width: '100%',
        aspectRatio: '1.45 / 1',
      }}
      aria-label="TB Trading · Binimeliz"
    >
      <img
        src="/logo.png"
        alt=""
        draggable={false}
        className="absolute inset-0 w-full h-full pointer-events-none select-none"
        style={{
          objectFit: 'cover',
          // Centro óptico del contenido completo (TB hasta Binimeliz cursiva)
          // está alrededor de Y ≈ 53% de la imagen.
          objectPosition: 'center 53%',
        }}
      />
    </div>
  );
}

// SVG cuadrado simplificado para favicon + sidebar collapsed.
function BiniMarkSquareSVG(): ReactElement {
  return (
    <svg viewBox="0 0 64 64" fill="none" className="block w-full h-full" aria-hidden="true">
      <defs>
        <linearGradient id="sm-em" x1="0" y1="0" x2="100%" y2="100%">
          <stop offset="0" stopColor="#6ee7b7" />
          <stop offset="0.5" stopColor="#10b981" />
          <stop offset="1" stopColor="#047857" />
        </linearGradient>
        <linearGradient id="sm-sl" x1="0" y1="0" x2="100%" y2="100%">
          <stop offset="0" stopColor="#f5f5f5" />
          <stop offset="0.5" stopColor="#a3a3a3" />
          <stop offset="1" stopColor="#525252" />
        </linearGradient>
      </defs>
      <text x="14" y="44" fontFamily="'Inter', system-ui, sans-serif" fontWeight="900" fontSize="42" fill="url(#sm-em)" textAnchor="middle">T</text>
      <text x="50" y="44" fontFamily="'Inter', system-ui, sans-serif" fontWeight="900" fontSize="42" fill="url(#sm-sl)" textAnchor="middle">B</text>
      <line x1="32" y1="14" x2="32" y2="20" stroke="url(#sm-em)" strokeWidth="1.4" strokeLinecap="round" />
      <line x1="32" y1="40" x2="32" y2="48" stroke="url(#sm-em)" strokeWidth="1.4" strokeLinecap="round" />
      <rect x="29.5" y="20" width="5" height="20" rx="0.8" fill="url(#sm-em)" />
      <path d="M 6 58 L 22 54 L 32 56 L 50 50" stroke="url(#sm-em)" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M 46 48 L 52 50 L 50 56" stroke="url(#sm-em)" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
